import type { SalesExplorerFilters } from "./types";

export interface ChipLookups {
  appointmentStatuses: { id: string; name: string }[];
  appointmentTypes: { id: string; name: string }[];
  breeds: { id: string; name: string }[];
  categories: { id: string; name: string }[];
  items: { id: string; name: string }[];
  professionals: { id: string; name: string }[];
  sizeCategories: { id: string; name: string }[];
  species: { id: string; name: string }[];
}

export interface ChipLabels {
  appointmentStatus: string;
  appointmentType: string;
  breed: string;
  category: string;
  financialStatus: string;
  item: string;
  itemCount: string;
  itemType: string;
  ownerCity: string;
  ownerNeighborhood: string;
  ownerSearch: string;
  professional: string;
  size: string;
  species: string;
  weight: string;
}

export interface ActiveChip {
  id: string;
  label: string;
  value: string;
  /** Devolve os filtros sem este chip. Sempre volta para a página 1: a página
   *  atual pode não existir no recorte menor. */
  remove: (filters: SalesExplorerFilters) => SalesExplorerFilters;
}

type ListKey = "appointmentTypeIds" | "appointmentStatuses" | "professionalIds" | "itemIds" | "categoryIds" | "speciesIds" | "breedIds" | "sizeCategoryIds";

const nameOf = (list: { id: string; name: string }[], id: string) =>
  list.find((entry) => entry.id === id)?.name ?? id;

const OPERATOR_SYMBOL = { eq: "=", gte: "≥", lte: "≤" } as const;

export function buildActiveChips(
  filters: SalesExplorerFilters,
  lookups: ChipLookups,
  labels: ChipLabels,
): ActiveChip[] {
  const chips: ActiveChip[] = [];

  const pushList = (key: ListKey, label: string, source: { id: string; name: string }[]) => {
    (filters[key] as string[]).forEach((id) => {
      chips.push({
        id: `${key}:${id}`,
        label,
        value: nameOf(source, id),
        remove: (current) => ({
          ...current,
          [key]: (current[key] as string[]).filter((value) => value !== id),
          page: 1,
        }),
      });
    });
  };

  pushList("appointmentTypeIds", labels.appointmentType, lookups.appointmentTypes);
  pushList("appointmentStatuses", labels.appointmentStatus, lookups.appointmentStatuses);
  pushList("professionalIds", labels.professional, lookups.professionals);

  if (filters.financialStatus !== "all") {
    chips.push({
      id: "financialStatus",
      label: labels.financialStatus,
      value: filters.financialStatus,
      remove: (current) => ({ ...current, financialStatus: "all", page: 1 }),
    });
  }

  pushList("itemIds", labels.item, lookups.items);

  filters.itemTypes.forEach((type) => {
    chips.push({
      id: `itemTypes:${type}`,
      label: labels.itemType,
      value: type,
      remove: (current) => ({
        ...current,
        itemTypes: current.itemTypes.filter((value) => value !== type),
        page: 1,
      }),
    });
  });

  pushList("categoryIds", labels.category, lookups.categories);

  if (filters.itemCount) {
    chips.push({
      id: "itemCount",
      label: labels.itemCount,
      value: `${OPERATOR_SYMBOL[filters.itemCount.operator]} ${filters.itemCount.value}`,
      remove: (current) => ({ ...current, itemCount: null, page: 1 }),
    });
  }

  pushList("speciesIds", labels.species, lookups.species);
  pushList("breedIds", labels.breed, lookups.breeds);
  pushList("sizeCategoryIds", labels.size, lookups.sizeCategories);

  if (filters.weightMin !== null || filters.weightMax !== null) {
    const { weightMax, weightMin } = filters;
    const value =
      weightMin !== null && weightMax !== null
        ? `${weightMin} – ${weightMax}`
        : weightMin !== null
          ? `≥ ${weightMin}`
          : `≤ ${weightMax}`;

    chips.push({
      id: "weight",
      label: labels.weight,
      value,
      remove: (current) => ({ ...current, weightMax: null, weightMin: null, page: 1 }),
    });
  }

  const pushText = (key: "ownerSearch" | "ownerCity" | "ownerNeighborhood", label: string) => {
    if (!filters[key].trim()) return;
    chips.push({
      id: key,
      label,
      value: filters[key].trim(),
      remove: (current) => ({ ...current, [key]: "", page: 1 }),
    });
  };

  pushText("ownerSearch", labels.ownerSearch);
  pushText("ownerCity", labels.ownerCity);
  pushText("ownerNeighborhood", labels.ownerNeighborhood);

  return chips;
}
